import { FulfillmentOption, noOp } from 'common/Constants';
import { isAvailableOnDate } from 'common/FoodItemAvailability';
import { getDataMealType, getMealTypeId } from 'common/MealTypeUtils';
import { assertDateString, urlDateFormat } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import QueryString from 'query-string';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, RouteChildrenProps } from 'react-router';
import { useReloadCartFoodItems } from 'src/cart/hooks/useReloadCartFoodItems';
import { CelebrityEventMaxOrderAlertBanner } from 'src/celebrity-event/CelebrityEventMaxOrderAlertBanner';
import { CelebrityEventPromoCodeBanner } from 'src/celebrity-event/CelebrityEventPromoCodeBanner';
import { PopularShefsCarousel } from 'src/celebrity-event/PopularShefsCarousel';
import { useChatContext } from 'src/chat/hooks/useChatContext';
import { useIsDishFirst } from 'src/dish-first/useIsDishFirst';
import {
  ConsumerOrderDishSelectionItemFragment,
  Packaging,
  useRecentTasteOrdersQuery,
} from 'src/gqlReactTypings.generated.d';
import { useDishFistCartPositionContext } from 'src/pages/consumer/components/ConsumerHeaderWrapper';
import { useCachedDishFirstFilters } from 'src/pages/consumer/dish-first/explore/hooks/useCachedDishFirst/useCachedDishFirstFilters';
import { useIsSoldOut } from 'src/pages/consumer/order/sold-out-notifications/useIsSoldOut';
import { ButtonColors } from 'src/shared/design-system/Buttons';
import { useHandleAddToCartDateUpdates } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { FollowColorsType } from 'src/shared/design-system/FollowShef';
import { Container } from 'src/shared/design-system/Layout/Container';
import { Row } from 'src/shared/design-system/Layout/Row';
import { useFirstTimeExitIntent } from 'src/shared/hooks/useFirstTimeExitIntent';
import { Notifications } from 'src/shared/Notifications';
import { useDispatch, useSelector } from 'src/store';
import { clearCarts, decLineItemQuantity, incLineItemQuantity } from 'src/store/cart/actions';
import { selectCart, selectShefLineItemsById } from 'src/store/cart/selectors';
import { CartFoodItem } from 'src/store/cart/types';
import { CollectionAnchor } from '../../dish-first/explore/components/Segments/DishFirstCardCollection/styles';
import { MEAL_TYPE_SELECTOR } from '../../dish-first/explore/Constants';
import { MealTypeMenuBarProvider } from '../../dish-first/explore/context';
import { FollowButton } from '../../follow-shef/FollowButton';
import { ConsumerOrderModals } from '../ConsumerOrderModals';
import { ShefMenuSegmentRow } from '../shef-menu/ShefMenuSegmentRow';
import { useIsReviewsExperience } from '../useIsReviewsExperience';
import { TabNames } from './constants';
import ConsumerOrderBanner from './ConsumerOrderBanner';
import { ConsumerOrderCart } from './ConsumerOrderCart';
import { ConsumerOrderDishSelection } from './ConsumerOrderDishSelection';
import { FilteredConsumerOrderDishSelection } from './ConsumerOrderDishSelection/FilteredConsumerOrderDishSelection';
import { ConsumerOrderDishSelectionRow } from './ConsumerOrderDishSelectionRow';
import { ConsumerOrderShefStories } from './ConsumerOrderShefStories';
import { ConsumerOrderShefVideoDetails } from './ConsumerOrderShefVideoDetails';
import { ConsumerOrderUpdateDeliveryDateModal } from './ConsumerOrderUpdateDeliveryDateModal';
import { DishFirstConsumerOrderFilters } from './DishFirstConsumerOrderFilters';
import { EditingBanner } from './EditingBanner';
import { getRatingPercentForReview } from './getRatingPercentForReview';
import { MenuReviewCard, MenuReviewCards } from './MenuReviewCard';
import { ProminentDeliveryDatePicker } from './ProminentDeliveryDatePicker';
import { ReviewsModal } from './ReviewsModal';
import { OpenReviewsButton } from './ReviewsModal/OpenReviewsButton';
import {
  ConsumerOrderCartCol,
  ContainerBackgroundColor,
  DishSelectionWrapper,
  MainRow,
  MenuCol,
  OrderOneTime,
  ShowMoreButton,
  ShowMoreButtonRow,
  ShowMoreButtonWrapper,
  TopBorder,
} from './styles';
import { IConsumerOrderProps } from './types';
import { useConsumerOrderTracking } from './useConsumerOrderTracking';

export const ConsumerOrder: React.FC<IConsumerOrderProps & RouteChildrenProps> = (props) => {
  const {
    shefId,
    showDishTags,
    setDeliveryDate,
    deliveryDate,
    availableFoodItems,
    allFoodItems,
    availabilityInfo,
    shef,
    location,
    match,
    nextStepLocation,
    isEditingOrder,
    onIncrement,
    activeTab,
    segments = [],
    setTab = noOp,
    reviews = [],
    hasMoreFoodItems = false,
    showAllFoodItems = noOp,
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  useFirstTimeExitIntent();
  const deliveryUrlDate = deliveryDate ? deliveryDate.toFormat(urlDateFormat) : undefined;
  const cartPositionState = useDishFistCartPositionContext();

  useReloadCartFoodItems();
  useConsumerOrderTracking(props);
  const { openWidget } = useChatContext();

  const dispatch = useDispatch();

  const { areFiltersEmpty } = useCachedDishFirstFilters();

  const { shefLineItems, isMultiCart } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    return {
      shefLineItems: selectShefLineItemsById(cart, shefId),
      isMultiCart: cart.isMultiCart,
    };
  });

  const { cartSize } = useSelector((state) => {
    const lineItems = isEditingOrder ? state.editableOrderCart : state.cart;
    const cartSize = lineItems?.lineItems?.ids?.length;
    return { cartSize };
  });

  const { isShefAvailable, isCartEmpty, nextDeliveryDate, isShefAvailableInFuture } = useMemo(() => {
    const isFlexible = isEmpty(deliveryDate);
    const isShefAvailable =
      (isFlexible && availabilityInfo.length > 0) ||
      !!availabilityInfo.find((day) => deliveryDate && day.dateTime.equals(deliveryDate));
    const nextDeliveryDate =
      availabilityInfo.find((day) => !day.isSoldOut && day.numDishesAvailable > 0)?.dateTime || null;
    const isCartEmpty = cartSize === 0;
    return {
      isShefAvailable,
      isCartEmpty,
      nextDeliveryDate,
      isShefAvailableInFuture: !!availabilityInfo.length,
    };
  }, [deliveryDate, availabilityInfo, cartSize]);

  const { modalVisible: isSoldOut } = useIsSoldOut({
    availabilityInfo,
    deliveryDate,
    shefId,
    isEditingOrder,
  });

  const nextDeliveryDateFoodItem = useMemo(
    () =>
      allFoodItems.filter((item) => {
        if (!nextDeliveryDate) return true;
        if (!item) return false;
        return isAvailableOnDate(item, nextDeliveryDate, shef.shefProfile.timeZone);
      }),
    [shef.timeZone, nextDeliveryDate, allFoodItems]
  );

  const { data } = useRecentTasteOrdersQuery();
  const recentTasteTests = data?.me?.recentTasteTests ?? 0;
  const isAdmin = data?.me?.admin ?? false;

  const { isTrialShef, fulfillmentOption } = shef.shefProfile;
  const isPickupOrder = fulfillmentOption === FulfillmentOption.Pickup;

  const shefDeliveryDate = isShefAvailable ? deliveryUrlDate : nextDeliveryDate?.toFormat(urlDateFormat);
  const dateTimeShefDeliveryDate = shefDeliveryDate ? deliveryDate : undefined;

  const dateStringShefDeliveryDate = dateTimeShefDeliveryDate
    ? assertDateString(dateTimeShefDeliveryDate.toISODate())
    : undefined;

  const { handleDeliveryDateUpdates } = useHandleAddToCartDateUpdates(dateStringShefDeliveryDate);

  // FUTURE TODO: would be best to include this logic in the cart redux action/reducer logic
  const increment = useCallback(
    (foodItem: ConsumerOrderDishSelectionItemFragment): void => {
      if (!isEditingOrder && !isAdmin && isTrialShef && recentTasteTests >= 5) {
        Notifications.error('Cannot taste test more than 5 times in a month!');
        return;
      }
      const shouldIncrement = onIncrement ? onIncrement() : true;
      const itemAvailability = foodItem.availability.find(({ numAvailable }) => (numAvailable ?? 0) > 0);
      const dishDeliveryDate = itemAvailability?.availabilityDate
        ? DateTime.fromISO(itemAvailability.availabilityDate).toFormat(urlDateFormat)
        : null;
      const itemDeliveryDate = shefDeliveryDate ?? dishDeliveryDate;

      if (shouldIncrement && itemDeliveryDate) {
        dispatch(
          incLineItemQuantity(isEditingOrder, {
            foodItem,
            location: 'consumer-order',
            deliveryDate: itemDeliveryDate,
          })
        );
      }

      if (itemDeliveryDate) {
        handleDeliveryDateUpdates(assertDateString(itemDeliveryDate));
      }
    },
    [
      isAdmin,
      shefDeliveryDate,
      dispatch,
      incLineItemQuantity,
      isEditingOrder,
      isTrialShef,
      dispatch,
      onIncrement,
      recentTasteTests,
      isCartEmpty,
    ]
  );

  const decrementQuantity = useCallback(
    (foodItem: CartFoodItem) => {
      if (shefDeliveryDate) {
        dispatch(
          decLineItemQuantity(isEditingOrder, {
            foodItem,
            deliveryDate: shefDeliveryDate,
            location: 'consumer-order',
          })
        );
      }
    },
    [dispatch, isEditingOrder, shefDeliveryDate]
  );

  const [showFinalize, setShowFinalize] = useState<boolean | undefined>();

  const tabOptions = useMemo(
    () => ({
      options: Object.values(TabNames),
      selected: activeTab,
      onChange: setTab,
    }),
    [activeTab, setTab]
  );

  const { isDishFirst } = useIsDishFirst();
  const parsedQs = QueryString.parse(location.search);
  const urlDate = parsedQs[QueryParams.DATE];

  const { shouldUseReviews } = useIsReviewsExperience(shef.rating?.count ?? 0, shef.shefProfile.isTrialShef);

  const ogTitle = `Shef | ${shef.firstName}'s Menu`;
  const ogImage = shef.shefProfile.coverPhotoUrl ?? shef.imageUrl;

  const validReviewDishes = availableFoodItems
    .map((foodItem) => foodItem.slug)
    .filter((slug): slug is string => Boolean(slug));

  const hasCompostablePackaging = useMemo(() => {
    const compostableItems = availableFoodItems.filter((item) => item.packaging === Packaging.Compostable);
    return compostableItems.length > 0;
  }, [availableFoodItems]);

  const showReviews = reviews.length > 0 && shouldUseReviews && !shef.celebrityEventType;

  const messageShef = useCallback(() => {
    if (openWidget) {
      openWidget(shef);
    }
  }, [openWidget, shef]);

  const shouldShowShefMenuSegments = isDishFirst && areFiltersEmpty;

  const setDateToNextDeliveryDate = useCallback(() => {
    if (nextDeliveryDate) {
      setDeliveryDate(nextDeliveryDate);
    }
  }, [setDeliveryDate, nextDeliveryDate]);

  const setDateToNextDeliveryDateAndClearCart = useCallback(() => {
    if (nextDeliveryDate) {
      dispatch(clearCarts());
      setDeliveryDate(nextDeliveryDate);
    }
  }, [nextDeliveryDate, dispatch, setDeliveryDate]);

  const consumerDishSelectionProps = {
    shefId,
    shef,
    availabilityInfo,
    deliveryDate: dateTimeShefDeliveryDate,
    deliveryDateTime: dateTimeShefDeliveryDate,
    showDishTags,
    increment,
    decrement: decrementQuantity,
    cartLineItems: shefLineItems,
    isUnavailable: !availabilityInfo.length,
    match,
    location,
    isEditingOrder,
    isDishFirst,
    isShefMenu: !isEditingOrder,
    isShefAvailable: isShefAvailable && !isSoldOut,
    nextDeliveryDate,
    isShefAvailableInFuture,
    openModal,
    showModalPrompt: !isShefAvailable,
    foodItems: isShefAvailable ? availableFoodItems : nextDeliveryDateFoodItem,
    attachAnchors: true,
  };

  const finalDeliveryPrice = shef.deliveryPrice?.finalDeliveryPrice;

  return (
    <MealTypeMenuBarProvider>
      <MainRow isMultiCart={isMultiCart} style={{ marginRight: 0, marginLeft: 0 }} data-cy={`shef-${shef.id}`}>
        <Helmet>
          <title>{ogTitle}</title>
          <meta property='og:title' content={ogTitle} />
          <meta property='og:url' content={`${window.location.origin}/${location.pathname}`} />
          <link rel='canonical' href={`${window.location.origin}/order/shef/${shef.id}`} />
          {shef.shefProfile.bio && <meta property='og:description' content={shef.shefProfile.bio} />}
          {ogImage && <meta property='og:image' content={ogImage} />}
        </Helmet>
        <ConsumerOrderModals
          availabilityInfo={availabilityInfo}
          deliveryDate={deliveryDate}
          shefId={shef.id}
          shefName={shef.firstName ?? ''}
          shefImageUrl={shef.imageUrl}
          isTrialShef={isTrialShef}
          isEditingOrder={isEditingOrder}
        />
        <MenuCol isMultiCart={isMultiCart} md='9' style={{ paddingRight: 0, paddingLeft: 0 }}>
          {isEditingOrder && <EditingBanner />}
          <OrderOneTime isMultiCart={isMultiCart}>
            <ContainerBackgroundColor showDatePicker={!isDishFirst}>
              <TopBorder />
              <ConsumerOrderShefVideoDetails
                shef={shef}
                foodItemImageUrl={availableFoodItems[0]?.imageUrl ?? ''}
                radioButtonProps={tabOptions}
                finalDeliveryPrice={finalDeliveryPrice}
                hasMultipleMenus={false}
                hasCompostableItems={hasCompostablePackaging}
                hasHiddenFoodItems={hasMoreFoodItems}
              />
              <TopBorder />
            </ContainerBackgroundColor>
            {!isDishFirst && deliveryDate && (
              <ProminentDeliveryDatePicker
                availabilityInfo={availabilityInfo}
                currentDeliveryDateTime={deliveryDate}
                isPickupOrder={isPickupOrder}
                onDateChanged={setDeliveryDate}
                hasUrlDeliveryDate={urlDate != null}
              />
            )}
            <ConsumerOrderBanner
              isShefAvailable={isShefAvailable && !isSoldOut}
              isCartEmpty={isCartEmpty}
              isShefAvailableInFuture={isShefAvailableInFuture}
              nextDeliveryDate={nextDeliveryDate}
              messageShef={messageShef}
              shefAllowedToChat={(!shef.celebrityEventType && shef.shefProfile.allowedToChat) || false}
              setDateToNextDeliveryDate={setDateToNextDeliveryDate}
            />
            <ConsumerOrderUpdateDeliveryDateModal
              nextDeliveryDate={nextDeliveryDate}
              isShowing={modalOpen && (!isShefAvailable || isSoldOut)}
              onClose={closeModal}
              isCartEmpty={isCartEmpty}
              onClick={isCartEmpty ? setDateToNextDeliveryDate : setDateToNextDeliveryDateAndClearCart}
            />
            {!isEditingOrder ? <CelebrityEventMaxOrderAlertBanner shef={shef} /> : null}
            {!isEditingOrder ? <CelebrityEventPromoCodeBanner celebrityEventType={shef.celebrityEventType} /> : null}

            <Container style={{ maxWidth: '100%', height: 'inherit' }}>
              <Row>
                <DishSelectionWrapper md='12'>
                  {!shef.celebrityEventType ? (
                    <DishFirstConsumerOrderFilters
                      isDishFirst={isDishFirst}
                      isEditingOrder={isEditingOrder}
                      shefId={shef.id}
                    />
                  ) : null}
                  {shouldShowShefMenuSegments &&
                    segments.map((segment) => {
                      if (segment.content.total <= 0) return null;
                      return (
                        <React.Fragment key={segment.id}>
                          <CollectionAnchor
                            data-role={MEAL_TYPE_SELECTOR}
                            data-meal-type={getDataMealType(segment.id)}
                            id={getMealTypeId(segment.id)}
                          />
                          <ShefMenuSegmentRow
                            segment={segment}
                            location={location}
                            deliveryDateTime={deliveryDate}
                            isUnavailable={!availabilityInfo.length}
                            match={match}
                            increment={increment}
                            decrement={decrementQuantity}
                            shef={shef}
                            isEditingOrder={isEditingOrder}
                            isShefAvailable={isShefAvailable && !isSoldOut}
                            nextDeliveryDate={nextDeliveryDate}
                            isShefAvailableInFuture={isShefAvailableInFuture}
                            openModal={openModal}
                            showModalPrompt={!isShefAvailable}
                          />
                        </React.Fragment>
                      );
                    })}
                  <Row>
                    <DishSelectionWrapper md='12'>
                      {areFiltersEmpty ? (
                        <>
                          <ConsumerOrderDishSelection
                            shefSegments={segments.filter((segment) => segment.content.total >= 0)}
                            {...consumerDishSelectionProps}
                          />
                          {hasMoreFoodItems && (
                            <ShowMoreButtonRow>
                              <ShowMoreButtonWrapper sm={4}>
                                <ShowMoreButton onClick={showAllFoodItems} hasReviews={showReviews}>
                                  Show All Items
                                </ShowMoreButton>
                              </ShowMoreButtonWrapper>
                            </ShowMoreButtonRow>
                          )}
                        </>
                      ) : (
                        <>
                          <FilteredConsumerOrderDishSelection {...consumerDishSelectionProps} />
                        </>
                      )}

                      {shef.celebrityEventType ? <PopularShefsCarousel shef={shef} className='mb-10' /> : null}
                      {showReviews && (
                        <ConsumerOrderDishSelectionRow
                          title={`REVIEWS (${shef.rating?.count ?? 0})`}
                          button={<OpenReviewsButton />}
                          blur={false}>
                          <Container fullWidth>
                            <MenuReviewCards>
                              {reviews.map((review) => (
                                <MenuReviewCard
                                  comment={review.comment ?? null}
                                  deliveryDate={review.deliveryDate}
                                  key={review.id}
                                  name={review.user.publicName}
                                  timezone={shef.timeZone}
                                  ratingPercent={getRatingPercentForReview(review)}
                                />
                              ))}
                            </MenuReviewCards>
                          </Container>
                        </ConsumerOrderDishSelectionRow>
                      )}
                      {!shef.celebrityEventType && (
                        <ConsumerOrderDishSelectionRow
                          title={
                            <>
                              <span>Shef {shef.firstName} Stories</span>
                              <FollowButton
                                color={ButtonColors.GRAY}
                                followingColor={FollowColorsType.WHITE}
                                name={shef.publicName}
                                shefId={shef.id}
                                imageUrl={shef.imageUrl}
                                location='menu'
                              />
                            </>
                          }
                          blur={false}>
                          <ConsumerOrderShefStories shef={shef} />
                        </ConsumerOrderDishSelectionRow>
                      )}
                    </DishSelectionWrapper>
                  </Row>
                </DishSelectionWrapper>
              </Row>
              {deliveryDate !== undefined && showFinalize && <Redirect to={nextStepLocation} push={true} />}
            </Container>
          </OrderOneTime>
        </MenuCol>
        <ConsumerOrderCartCol isMultiCart={isMultiCart} open={cartPositionState.isOpen}>
          <ConsumerOrderCart
            foodItems={availableFoodItems}
            shef={shef}
            checkout={setShowFinalize}
            deliveryDate={deliveryDate}
            setDeliveryDate={setDeliveryDate}
            match={match}
            location={location}
            isEditingOrder={isEditingOrder}
          />
          <ReviewsModal shefId={shef.id} validDishes={validReviewDishes} />
        </ConsumerOrderCartCol>
      </MainRow>
    </MealTypeMenuBarProvider>
  );
};
